import React, { useState, useEffect, useContext } from 'react';
import Popup from '../Popup';
import PrimaryButton from '../PrimaryButton';
import TextField from '../TextField';
import ConfirmPopup from '../ConfirmPopup';
import useApi from '../../hooks/useApi';
import { GlobalContext } from '../../GlobalContext';
import {
  PopupColumnWrapper,
  ColumnWrapper,
  LibraryHeader,
  GeneralLibraryHeader,
  PublishOrganisationWrapper,
  LibraryName,
  PublishInfo,
  UpdateInfo,
  ButtonPanel,
  UserWrapper,
  UserToggle,
  UserButtonPanel,
  UserInfoPanel,
  Profile,
  ProfileAvatar,
  ProfileName,
  ByLine,
  Bio,
  Info,
} from './styles';

const PublishPopup = ({ userToken, entityType, entityId, popupIsOpen, onClose, flash, fileService }) => {
  const context = useContext(GlobalContext);
  const { getData, postData, loading } = useApi(userToken);
  const [publishingData, setPublishingData] = useState();
  const [showUserInfo, setShowUserInfo] = useState(false);
  const [user, setUser] = useState();
  const [profileByline, setProfileByline] = useState('');
  const [profileBio, setProfileBio] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);

  const route = entityType === 'loop' ? 'my-loops' : 'collections';

  const getPublishingData = async () => {
    const response = await getData(`/${route}/${entityId}/publish`);
    if (response.success) {
      const data = response.body;
      setUser(data.user);

      const { byline, bio } = data.user;
      setProfileByline(byline);
      setProfileBio(bio);
      if (entityType === 'loop' && (!byline || !bio)) {
        setShowUserInfo(true);
      }

      const libraryTypes = [];
      data.libraries.forEach((library) => {
        if (library.type === 'lin') {
          if (library.requires_review) {
            library.type = 'curated';
          } else {
            library.type = 'open';
          }
        } else {
          library.type = 'organisation';
        }
        if (!libraryTypes.includes(library.type)) {
          libraryTypes.push(library.type);
        }
      });
      data.libraryTypes = libraryTypes;
      setPublishingData(data);
    }
  };

  useEffect(() => {
    if (popupIsOpen) {
      getPublishingData();
    }
  }, [popupIsOpen]);

  useEffect(() => {
    const bottomPanel = document.querySelector('#bottomPanel');
    if (showUserInfo && bottomPanel) {
      bottomPanel.scrollIntoView();
    }
  }, [showUserInfo]);

  const {
    libraryTypes = [],
    libraries = [],
    lin_library,
    remixed: isRemixed,
    can_publish: can_publish_general,
    in_review: in_review_general,
    link_to_published: link_to_published_general,
  } = publishingData || {};

  const showFlashMessage = (message) => {
    if (flash) {
      flash(message, { type: 'status' });
    } else {
      context.showFlashMessage(i18n.__(message));
    }
  };

  const showFlashError = (message) => {
    if (flash) {
      flash(message, { type: 'error' });
    } else {
      context.showFlashError(i18n.__(message));
    }
  };

  const verifyProfileData = (saveProfile) => {
    if (entityType === 'loop' && (!profileByline || !profileBio)) {
      const messageKey = saveProfile ? 'publish_info_missing_data' : 'publish_info_required_message';
      showFlashMessage(i18n.__(messageKey || 'publish_info_required_message'));
      setShowUserInfo(true);
      return false;
    } else {
      return true;
    }
  };

  const publish = async (id, requiresReview) => {
    if (!verifyProfileData(false)) {
      return;
    }
    const res = await postData(`/${route}/${entityId}/publish/${id}`);
    if (res.success) {
      await getPublishingData();
      onClose();
      const message = requiresReview ? `${entityType}_publish_success` : `${entityType}_scoped_publish_success`;
      showFlashMessage(i18n.__(message));
    } else {
      console.error(res);
      const message = requiresReview
        ? `error_publish_${entityType}_failed`
        : `error_scoped_publish_${entityType}_failed`;
      showFlashError(i18n.__(message));
    }
  };

  const getHeader = (libraryType) => {
    const Component = libraryType === 'lin' ? GeneralLibraryHeader : LibraryHeader;
    const localeLibraryString = ['open', 'curated'].includes(libraryType) ? `shared_${libraryType}` : libraryType;
    return <Component>{i18n.__(`publish_loop_modal_${localeLibraryString}_header`)}</Component>;
  };

  const getMappedLibrary = (library) => {
    const {
      id,
      name,
      can_publish: can_publish_local,
      in_review: in_review_local,
      link_to_published: link_to_published_local,
      requires_review,
      type: libraryType,
      read_scope: readScope,
    } = library;

    const isOpen = libraryType !== 'organisation';
    const isCurated = libraryType === 'curated';
    const isLinSweden = libraryType === 'lin' && readScope === 'sweden';
    const canPublish = isOpen ? can_publish_general : can_publish_local;
    const inReview = isOpen ? in_review_general : in_review_local;
    const linkToPublished = libraryType === 'lin' ? link_to_published_general : link_to_published_local;

    let info = i18n.__(`publish_${entityType}_modal_organization_body`);
    let buttonLabel = i18n.__(`publish_loop_modal_organisation_publish_btn`);
    let disabled = false;

    if (isLinSweden) {
      buttonLabel = i18n.__(`publish_loop_modal_general_handin`);
      disabled = true;
      if (canPublish) {
        if (isRemixed) {
          info = i18n.__(`publish_${entityType}_modal_remixed_body`);
        } else if (inReview) {
          info = i18n.__(`publish_${entityType}_modal_in_review_body`);
        } else {
          disabled = false;
          info = i18n.__(`publish_${entityType}_modal_general_body`);
        }
      } else {
        info = i18n.__(`error_publish_${entityType}_not_generally_visible`);
      }
    } else if ((isOpen || isCurated) && canPublish) {
      info = i18n.__(i18n.__(`publish_${entityType}_modal_open_body`, name));
    } else if (!isOpen && canPublish) {
      info = i18n.__(`publish_${entityType}_modal_organization_body`);
    } else {
      disabled = true;
      if (isOpen) {
        info = i18n.__(`error_publish_${entityType}_not_generally_visible`);
      } else {
        info = i18n.__(`error_publish_${entityType}_org_not_generally_visible`);
      }
    }

    return (
      <PublishOrganisationWrapper key={id}>
        <PublishInfo dangerouslySetInnerHTML={{ __html: info }} />
        {(isOpen || isCurated) && !isLinSweden && <LibraryName>{name}</LibraryName>}
        <ButtonPanel>
          <UpdateInfo
            dangerouslySetInnerHTML={{
              __html: linkToPublished && !disabled ? i18n.__(`publish_${entityType}_modal_updated_loop`) : '&nbsp;',
            }}
          />
          <PrimaryButton
            text={buttonLabel}
            onClick={isOpen && !isLinSweden ? () => setShowConfirm(true) : () => publish(id, requires_review)}
            data-id={id}
            data-requires-review={requires_review}
            disabled={disabled}
          />
        </ButtonPanel>
        {isOpen && !isLinSweden && (
          <ConfirmPopup
            popupIsOpen={showConfirm}
            id={`confirmPopup_${id}`}
            message={i18n.__(`confirm_publish_${entityType}_to_shared_library`)}
            onConfirm={() => publish(id, requires_review)}
            onCancel={() => setShowConfirm(false)}
          />
        )}
      </PublishOrganisationWrapper>
    );
  };

  const getMappedLibraries = (libraryType) =>
    libraries.filter((library) => library.type === libraryType).map((library) => getMappedLibrary(library));

  const mapLibraries = (libraryType) => (
    <ColumnWrapper key={libraryType}>
      {getHeader(libraryType)}
      {getMappedLibraries(libraryType)}
    </ColumnWrapper>
  );

  const toggleUserInfo = (event) => {
    event.target.blur();
    setShowUserInfo(!showUserInfo);
  };

  const saveProfile = async () => {
    if (!verifyProfileData(true)) {
      return;
    }
    const response = await postData(`/my-loops/publish_info`, {
      byline: profileByline,
      bio: profileBio,
    });
    if (response.success) {
      showFlashMessage(i18n.__('publish_info_saved'));
      setShowUserInfo(false);
      await getPublishingData();
    } else {
      showFlashError(i18n.__('error_saving_publish_info'));
    }
  };

  const userPanel = !user ? null : (
    <UserWrapper>
      <UserToggle tabIndex={0} show={showUserInfo} onClick={toggleUserInfo}>
        {showUserInfo ? i18n.__('publish_info_hide') : i18n.__('publish_info_show')}
      </UserToggle>
      <UserInfoPanel show={showUserInfo}>
        {!user.image._url && <Info dangerouslySetInnerHTML={{ __html: i18n.__('publish_info_image_missing') }} />}
        <Profile>
          <ProfileAvatar src={fileService.buildImageUrl(user.image._url)} />
          <ProfileName>{user.full_name}</ProfileName>
        </Profile>
        <ByLine>
          <TextField
            label={i18n.__('publish_info_byline_label')}
            id="byline"
            type="text"
            value={profileByline || ''}
            name="byline"
            aria-required="true"
            placeholder={i18n.__('default_texteditor_placeholder')}
            onChange={(event) => setProfileByline(event.target.value)}
          />
        </ByLine>
        <Bio>
          <label htmlFor="bio">{i18n.__('publish_info_bio_label')}</label>
          <textarea
            id="bio"
            name="bio"
            aria-required="true"
            placeholder={i18n.__('default_texteditor_placeholder')}
            onChange={(event) => setProfileBio(event.target.value)}
            defaultValue={profileBio || ''}
          />
        </Bio>
        <UserButtonPanel id="bottomPanel">
          <PrimaryButton text={i18n.__('app_save')} onClick={saveProfile} disabled={loading} />
        </UserButtonPanel>
      </UserInfoPanel>
    </UserWrapper>
  );

  const contentPanel = (
    <PopupColumnWrapper>
      {lin_library && (
        <ColumnWrapper>
          {getHeader('general')}
          {getMappedLibrary(lin_library)}
        </ColumnWrapper>
      )}
      {libraryTypes.map((libraryType) => mapLibraries(libraryType))}
      {userPanel}
    </PopupColumnWrapper>
  );

  return publishingData ? (
    <Popup
      popupIsOpen={popupIsOpen}
      id={`publish_${entityType}_popup`}
      heading={i18n.__(`publish_${entityType}_modal_title`)}
      contentPanel={contentPanel}
      onClose={onClose}
      width={600}
    />
  ) : null;
};

export default PublishPopup;
